/**
 * translate() without React: the pure part of the i18n layer (index.tsx wraps it in a context), so
 * unit tests can format messages exactly as the app does.
 */
import type { Lang } from '../../shared/dto/auth';
import type { Msg } from './define';
import { MESSAGES, type MessageKey } from './registry';

export type Vars = Record<string, string | number>;
export type T = (key: MessageKey, vars?: Vars) => string;

export function locale(lang: Lang): string {
  return lang === 'is' ? 'is-IS' : 'en-GB';
}

function interpolate(s: string, vars?: Vars): string {
  if (!vars) return s;
  return s.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m));
}

const pluralCache = new Map<Lang, Intl.PluralRules>();
function pluralRules(lang: Lang) {
  let pr = pluralCache.get(lang);
  if (!pr) {
    pr = new Intl.PluralRules(locale(lang));
    pluralCache.set(lang, pr);
  }
  return pr;
}

export function translate(lang: Lang, key: MessageKey, vars?: Vars): string {
  const table = MESSAGES[lang] as Record<string, Msg>;
  const msg = table[key] ?? (MESSAGES.is as Record<string, Msg>)[key] ?? key;
  if (typeof msg === 'string') return interpolate(msg, vars);
  const n = Number(vars?.count ?? 0);
  const form = pluralRules(lang).select(n) === 'one' ? msg.one : msg.other;
  return interpolate(form, vars);
}
